import React, { useState } from 'react'
import Typography from '@material-ui/core/Typography'
import TabContext from '@material-ui/lab/TabContext'
import TabList from '@material-ui/lab/TabList'
import Tab from '@material-ui/core/Tab'
import TabPanel from '@material-ui/lab/TabPanel'
import MaterialLink from '@material-ui/core/Link'
import clsx from 'clsx'
import { SERVICES_URL, useLocale, useTranslation } from '@plandi/common'

import { useNotificationTabContentStyles } from './notification-tab-content.styles'
import { ReactComponent as SettingIcon } from 'src/assets/setting-icon.svg'
import { NotificationItem } from '../notification-item'
import {
  useQueryCountNewNotifications,
  useQueryNotifications
} from '../notifications.query'
import { NOTIFICATIONS_SERVICE_TYPE } from '../notifications.constants'
import { useMutationReadAllNotification } from '../notifications.mutation'
import { components } from '../../../../generated'

type NotificationTabContentProps = {
  emptyMessage?: string
  withoutRead?: boolean
  className?: string
  tabPanelClassName?: string
  emptyMenuClass?: string
  notificationsPage?: boolean
}

export const NotificationTabContent: React.FC<NotificationTabContentProps> = (
  props
) => {
  const classes = useNotificationTabContentStyles()
  const url = SERVICES_URL

  const [serviceType, setServiceType] = useState<
    components['schemas']['AlertServiceType']
  >(NOTIFICATIONS_SERVICE_TYPE.OFFICE)
  const { data: translate } = useTranslation()
  const locale = useLocale()
  const { data: notifications, isLoading } = useQueryNotifications(
    serviceType,
    {
      select: (notifications) =>
        props.withoutRead
          ? notifications.filter((notification) => !notification.isRead)
          : notifications
    }
  )

  const { data: countNewNotification } = useQueryCountNewNotifications({
    select: (data) => data.count
  })

  const mutationReadAllNotification = useMutationReadAllNotification()

  const hasNotifications = notifications && notifications.length > 0

  const emptyMessage =
    props.emptyMessage ?? translate?.notifications['empty-notifications']

  const handleReadAllNotification = () => {
    mutationReadAllNotification.mutate(serviceType)
  }

  const NOTIFICATION_SERVICES_TYPE_OPTIONS = [
    ['office', translate?.notifications?.['short-office'] ?? 'Офис'],
    ['job', translate?.job?.title2 ?? 'Фриланс и работа'],
    ['shop', translate?.market?.title ?? 'Проектный маркет'],
    ['blog', translate?.blog ?? 'Блог']
  ]

  return (
    <div
      className={clsx({
        [classes.containerNotification]: !props.notificationsPage
      })}
    >
      <TabContext value={serviceType}>
        {!props.notificationsPage && (
          <div className={classes.notificationHeader}>
            <div>
              <MaterialLink
                color="textSecondary"
                className={classes.buttonRead}
                component="button"
                onClick={handleReadAllNotification}
              >
                {translate?.notifications['read-all']}
              </MaterialLink>
            </div>
            <div className={classes.notificationHeaderTitle}>
              {translate?.notifications['notifications-title']}
            </div>
            <MaterialLink
              className={classes.settingBtn}
              href={
                locale === 'ru'
                  ? `${url.main}/ru` + '/profile/notifications-settings'
                  : url.main + '/profile/notifications-settings'
              }
            >
              <SettingIcon />
            </MaterialLink>
          </div>
        )}
        <div className={props.className}>
          <TabList
            textColor="secondary"
            onChange={(_, value) => setServiceType(value)}
            variant="scrollable"
          >
            {NOTIFICATION_SERVICES_TYPE_OPTIONS.map(([key, value]) => {
              const hasNewNotification =
                (countNewNotification?.[
                  key as keyof typeof countNewNotification
                ] ?? 0) > 0

              return (
                <Tab
                  key={key}
                  label={value}
                  value={key}
                  className={clsx(classes.tab, {
                    [classes.badge]: hasNewNotification
                  })}
                />
              )
            })}
          </TabList>
          {props.notificationsPage && (
            <MaterialLink
              color="textSecondary"
              className={classes.buttonRead}
              component="button"
              onClick={handleReadAllNotification}
            >
              {translate?.notifications['read-all']}
            </MaterialLink>
          )}
        </div>
        {NOTIFICATION_SERVICES_TYPE_OPTIONS.map(([key]) => (
          <TabPanel
            key={key}
            value={key}
            className={clsx(classes.tabPanel, props.tabPanelClassName)}
          >
            {hasNotifications || isLoading ? (
              (notifications ?? [...new Array(10)])?.map(
                (notification, index) => (
                  <NotificationItem
                    key={notification?.id ?? `notifications${index}`}
                    {...notification}
                    isLoading={isLoading}
                  />
                )
              )
            ) : (
              <div className={clsx(classes.menuEmpty, props.emptyMenuClass)}>
                <Typography variant="h6">{emptyMessage}</Typography>
              </div>
            )}
          </TabPanel>
        ))}
      </TabContext>
    </div>
  )
}
